/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AdmissionApplicationDetailResponse } from '../models/AdmissionApplicationDetailResponse';
import type { AdmissionApplicationResponse } from '../models/AdmissionApplicationResponse';
import type { ApplicationStatusCountsResponse } from '../models/ApplicationStatusCountsResponse';
import type { BatchSendApplicationEmailRequest } from '../models/BatchSendApplicationEmailRequest';
import type { BatchUpdateApplicationStatusRequest } from '../models/BatchUpdateApplicationStatusRequest';
import type { Page_AdmissionApplicationResponse_ } from '../models/Page_AdmissionApplicationResponse_';
import type { SendApplicationEmailRequest } from '../models/SendApplicationEmailRequest';
import type { SuccessfulAdmissionApplicationResponse } from '../models/SuccessfulAdmissionApplicationResponse';
import type { UpdateApplicationStatusRequest } from '../models/UpdateApplicationStatusRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class AdmissionApplicationControllerService {
  /**
   * getApplicationsByParentAndWindow
   * @returns AdmissionApplicationResponse OK
   * @throws ApiError
   */
  public static getApplicationsByParentAndWindowUsingGet({
    admissionWindowId,
    parentId,
    search,
    status,
  }: {
    /**
     * admissionWindowId
     */
    admissionWindowId: string;
    /**
     * parentId
     */
    parentId: string;
    /**
     * search
     */
    search?: string;
    /**
     * status
     */
    status?:
      | 'ACCEPTED'
      | 'APPLIED'
      | 'AWAITING_PAYMENT'
      | 'REJECTED'
      | 'TEST_SCHEDULED'
      | 'UNDER_REVIEW';
  }): CancelablePromise<Array<AdmissionApplicationResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/admission-applications',
      query: {
        admissionWindowId: admissionWindowId,
        parentId: parentId,
        search: search,
        status: status,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * createApplicationWithoutPayment
   * @returns SuccessfulAdmissionApplicationResponse OK
   * @returns any Created
   * @throws ApiError
   */
  public static createApplicationWithoutPaymentUsingPost({
    files,
  }: {
    files?: Array<Blob>;
  }): CancelablePromise<SuccessfulAdmissionApplicationResponse | any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/admission-applications/apply-no-payment',
      formData: {
        files: files,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * createApplicationWithPayment
   * @returns SuccessfulAdmissionApplicationResponse OK
   * @returns any Created
   * @throws ApiError
   */
  public static createApplicationWithPaymentUsingPost({
    files,
  }: {
    files?: Array<Blob>;
  }): CancelablePromise<SuccessfulAdmissionApplicationResponse | any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/admission-applications/apply-with-payment',
      formData: {
        files: files,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * sendEmailToApplicants
   * @returns any OK
   * @throws ApiError
   */
  public static sendEmailToApplicantsUsingPost({
    request,
  }: {
    /**
     * request
     */
    request: BatchSendApplicationEmailRequest;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/admission-applications/batch/email',
      body: request,
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * batchUpdateStatus
   * @returns any OK
   * @throws ApiError
   */
  public static batchUpdateStatusUsingPost({
    request,
  }: {
    /**
     * request
     */
    request: BatchUpdateApplicationStatusRequest;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/admission-applications/batch/status',
      body: request,
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * getStatusCounts
   * @returns ApplicationStatusCountsResponse OK
   * @throws ApiError
   */
  public static getStatusCountsUsingGet({
    admissionWindowId,
  }: {
    /**
     * admissionWindowId
     */
    admissionWindowId: string;
  }): CancelablePromise<ApplicationStatusCountsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/admission-applications/status-counts',
      query: {
        admissionWindowId: admissionWindowId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * getApplicationsByWindow
   * @returns Page_AdmissionApplicationResponse_ OK
   * @throws ApiError
   */
  public static getApplicationsByWindowUsingGet({
    admissionWindowId,
    classLevelId,
    limit,
    offset,
    search,
    sortBy = 'dateCreated',
    sortDir = 'desc',
    status,
  }: {
    /**
     * admissionWindowId
     */
    admissionWindowId: string;
    /**
     * classLevelId
     */
    classLevelId?: string;
    limit?: number;
    offset?: number;
    /**
     * search
     */
    search?: string;
    /**
     * sortBy
     */
    sortBy?: string;
    /**
     * sortDir
     */
    sortDir?: string;
    /**
     * status
     */
    status?:
      | 'ACCEPTED'
      | 'APPLIED'
      | 'AWAITING_PAYMENT'
      | 'REJECTED'
      | 'TEST_SCHEDULED'
      | 'UNDER_REVIEW';
  }): CancelablePromise<Page_AdmissionApplicationResponse_> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/admission-applications/window/{admissionWindowId}',
      path: {
        admissionWindowId: admissionWindowId,
      },
      query: {
        classLevelId: classLevelId,
        limit: limit,
        offset: offset,
        search: search,
        sortBy: sortBy,
        sortDir: sortDir,
        status: status,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * getApplicationDetail
   * @returns AdmissionApplicationDetailResponse OK
   * @throws ApiError
   */
  public static getApplicationDetailUsingGet({
    applicationId,
  }: {
    /**
     * applicationId
     */
    applicationId: string;
  }): CancelablePromise<AdmissionApplicationDetailResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/admission-applications/{applicationId}',
      path: {
        applicationId: applicationId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * sendEmailToApplicant
   * @returns any OK
   * @throws ApiError
   */
  public static sendEmailToApplicantUsingPost({
    applicationId,
    request,
  }: {
    /**
     * applicationId
     */
    applicationId: string;
    /**
     * request
     */
    request: SendApplicationEmailRequest;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/admission-applications/{applicationId}/email',
      path: {
        applicationId: applicationId,
      },
      body: request,
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * updateStatus
   * @returns any OK
   * @throws ApiError
   */
  public static updateStatusUsingPut({
    applicationId,
    request,
  }: {
    /**
     * applicationId
     */
    applicationId: string;
    /**
     * request
     */
    request: UpdateApplicationStatusRequest;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/admission-applications/{applicationId}/status',
      path: {
        applicationId: applicationId,
      },
      body: request,
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }
}
